import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import PricingTable from '../components/PricingTable';
import CookieBanner from '../components/CookieBanner';
import { getAuth } from 'firebase/auth';
import Navbar from '../components/Navbar';
import { usePlan } from '../contexts/PlanContext';
import { useProducts } from '../hooks/useProducts';
import Button from '../components/ui/Button';

const PricingPage = () => {
  const [searchParams] = useSearchParams();
  const wasCancelled = searchParams.get('cancelled') === 'true';
  const { products, loading: productsLoading, error: productsError, retry: retryProducts } = useProducts();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const auth = getAuth();
  const { plan, hasSubscription } = usePlan();

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      setIsAuthenticated(!!user);
    });
    return () => unsubscribe();
  }, []);

  const faqItems = [
    {
      question: "Qual plano é mais popular?",
      answer: "Nosso plano Pro é o mais escolhido, oferecendo o melhor custo-benefício com mapas ilimitados, análise completa por IA e 20 mensagens por mês."
    },
    {
      question: "Posso alterar meu plano depois?",
      answer: "Sim! Você pode fazer upgrade ou downgrade do seu plano a qualquer momento, e a diferença será calculada automaticamente."
    },
    {
      question: "Como funciona a garantia de reembolso?",
      answer: "Oferecemos uma garantia de reembolso de 7 dias. Se você não estiver satisfeito, devolveremos 100% do seu dinheiro, sem questionamentos."
    },
    {
      question: "Preciso fornecer dados do cartão de crédito?",
      answer: "Sim, para planos pagos. Usamos processamento seguro de pagamentos através do Stripe, com criptografia de ponta a ponta."
    },
    {
      question: "Posso cancelar a qualquer momento?",
      answer: "Sim! Não há contratos longos. Você pode cancelar sua assinatura a qualquer momento, diretamente da sua conta."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
      <Navbar />

      {wasCancelled && (
        <div className="mb-6 p-4 bg-purple-50 border border-purple-200 rounded-xl text-center">
          <p className="text-purple-700">Pagamento cancelado. Voce pode escolher um plano quando quiser.</p>
        </div>
      )}

      {/* Header Section */}
      <header className="pt-32 pb-16 px-4 text-center relative overflow-hidden">
        <div className="absolute top-0 right-0 -translate-y-8 translate-x-8 w-96 h-96 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
        <div className="absolute bottom-0 left-0 translate-y-8 -translate-x-8 w-96 h-96 bg-gradient-to-br from-purple-200 to-pink-200 rounded-full filter blur-3xl opacity-30 animate-pulse"></div>
        
        <div className="relative max-w-4xl mx-auto space-y-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-6xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent"
          >
            Planos e Preços
          </motion.div>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-gray-600 max-w-2xl mx-auto"
          >
            Escolha o plano ideal para sua jornada astrológica. Cancele a qualquer momento.
          </motion.p>
        </div>
      </header>

      {/* Pricing Table Section */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          {productsLoading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-500 border-t-transparent"></div>
            </div>
          ) : productsError ? (
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">Nao foi possivel carregar os planos.</p>
              <Button onClick={retryProducts} size="sm">
                Tentar novamente
              </Button>
            </div>
          ) : (
            <div id="pricing">
            <PricingTable
              products={products}
              showComparison={true}
              currentSubscription={hasSubscription ? { role: plan } : null}
              isAuthenticated={isAuthenticated}
            />
            </div>
          )}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-4 bg-white/50">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Perguntas Frequentes
          </h2>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <motion.details
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <summary className="font-semibold cursor-pointer text-gray-800 hover:text-purple-600 transition-colors">
                  {item.question}
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">{item.answer}</p>
              </motion.details>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8">Comece Sua Jornada Astral Hoje</h2>
          <p className="mb-8 text-lg opacity-90">
            Junte-se a milhares de pessoas que já descobriram seu caminho nas estrelas
          </p>
          <Button
            variant="inverted"
            size="lg"
            onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
          >
            Escolher Meu Plano
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-purple-400 text-sm">&copy; {new Date().getFullYear()} MapaAstral.ai — Todos os direitos reservados.</p>
        </div>
      </footer>

      <CookieBanner />
    </div>
  );
};

export default PricingPage;